// Maps service IDs (used by plans + user permissions) to the dashboard
// route that section opens, and back. Used to:
//   - pick a default route after login that matches the user's permissions
//   - reject navigation to sections the user can't access
//
// Service IDs are strings (legacy compatibility): "1", "2", ...
// Source of truth lives in the menu definition in Navigation.vue.

export const SERVICE_PRIMARY_ROUTE: Record<string, string> = {
  "1": "/dashboard/push",
  "2": "/dashboard/chat",
  "3": "/dashboard/polls",
  "4": "/dashboard/sponsors",
  "6": "/dashboard/links",
  "9": "/dashboard/programming",
  "10": "/dashboard/settings",
  "11": "/dashboard/widget/1",
};

// Order in which sections appear in the sidebar — used to pick the first
// allowed one when redirecting on login.
export const SERVICE_ORDER = ["1", "2", "3", "6", "4", "11", "9", "10"];

// Match a path to the service it belongs to. Prefix-based so /dashboard/poll/edit/X
// still maps to service "3".
const PATH_TO_SERVICE: Array<[RegExp, string]> = [
  [/^\/dashboard\/push/, "1"],
  [/^\/dashboard\/chat/, "2"],
  [/^\/dashboard\/poll/, "3"],
  [/^\/dashboard\/trivia/, "3"],
  [/^\/dashboard\/sponsor/, "4"],
  [/^\/dashboard\/ad/, "4"],
  [/^\/dashboard\/link/, "6"],
  [/^\/dashboard\/podcast/, "6"],
  [/^\/dashboard\/program/, "9"],
  [/^\/dashboard\/settings/, "10"],
  [/^\/dashboard\/widget/, "11"],
];

export function serviceOfPath(path: string): string | null {
  for (const [re, id] of PATH_TO_SERVICE) if (re.test(path)) return id;
  return null;
}

// Routes that bypass per-section permissions and are gated by role only.
const ADMIN_ONLY_PATHS = ["/dashboard/radios", "/dashboard/projects", "/dashboard/plans", "/dashboard/covers"];
const RADIO_ADMIN_PATHS = ["/dashboard/users"];

export function getRoles(user: any): string[] {
  if (!user || !user.rol) return [];
  return String(user.rol).split("|").filter(Boolean);
}

export function getUserPermissions(user: any): string[] | null {
  if (!user) return null;
  if (Array.isArray(user.permissions)) return user.permissions.map(String);
  return null;
}

export function isRouteAllowed(user: any, path: string): boolean {
  const roles = getRoles(user);
  // Super-admin sees everything.
  if (roles.includes("admin")) return true;
  // Admin-only sections: only super-admins.
  if (ADMIN_ONLY_PATHS.some((p) => path.startsWith(p))) return false;
  // Radio-admin sections: super-admin or radio_admin.
  if (RADIO_ADMIN_PATHS.some((p) => path.startsWith(p))) {
    return roles.includes("radio_admin");
  }
  // Per-section permissions:
  const svc = serviceOfPath(path);
  if (!svc) return true; // unknown section => don't block (e.g. main dashboard)
  // radio_admin has full access to their radio's sections.
  if (roles.includes("radio_admin")) return true;
  const perms = getUserPermissions(user);
  if (!perms) return true; // legacy users without permissions array => full access
  return perms.includes(svc);
}

export function getDefaultRoute(user: any): string {
  const roles = getRoles(user);
  // Admins land on chat by default (unchanged behavior).
  if (roles.includes("admin") || roles.includes("radio_admin")) return "/dashboard/chat";
  const perms = getUserPermissions(user);
  if (!perms || !perms.length) {
    // No permissions and not admin — try chat, but it'll likely be blocked.
    return "/dashboard/chat";
  }
  for (const svc of SERVICE_ORDER) {
    if (perms.includes(svc) && SERVICE_PRIMARY_ROUTE[svc]) {
      return SERVICE_PRIMARY_ROUTE[svc];
    }
  }
  return "/dashboard/chat";
}
